import { useState } from 'react';
import toast from 'react-hot-toast';
import { flowFetch, FlowAttachment } from './flowApi';

function kind(mime?: string | null) {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf') return 'pdf';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  return 'other';
}
const isPreviewable = (mime?: string | null) => kind(mime) !== 'other';

function icon(mime?: string | null) {
  switch (kind(mime)) {
    case 'image': return '🖼️';
    case 'pdf': return '📄';
    case 'video': return '🎬';
    case 'audio': return '🎵';
    default: return '📎';
  }
}

function fmtSize(size?: number | null) {
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

// Visualizador inline (modal) para imagem, PDF, vídeo e áudio
function AttachmentPreview({ attachment, onClose }: { attachment: FlowAttachment; onClose: () => void }) {
  const k = kind(attachment.mimeType);
  return (
    <div className="fixed inset-0 z-[1500] flex flex-col bg-black/80 p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mb-3 flex items-center justify-between text-white" onClick={e => e.stopPropagation()}>
        <span className="truncate text-sm font-semibold">{attachment.fileName}</span>
        <div className="flex items-center gap-3">
          <a href={attachment.url} target="_blank" rel="noreferrer" className="text-xs underline hover:text-cyan-300">Abrir / baixar</a>
          <button onClick={onClose} className="rounded-full bg-white/10 p-1 hover:bg-white/20">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>
      <div className="flex flex-1 items-center justify-center overflow-hidden" onClick={e => e.stopPropagation()}>
        {k === 'image' && <img src={attachment.url} alt={attachment.fileName} className="max-h-full max-w-full rounded-lg object-contain" />}
        {k === 'pdf' && <iframe src={attachment.url} title={attachment.fileName} className="h-full w-full rounded-lg bg-white" />}
        {k === 'video' && <video src={attachment.url} controls className="max-h-full max-w-full rounded-lg" />}
        {k === 'audio' && <audio src={attachment.url} controls className="w-full max-w-lg" />}
      </div>
    </div>
  );
}

interface AttachmentListProps {
  attachments: FlowAttachment[];
  uploadPath: string;       // ex.: /api/flow/documents/:id/attachments
  onChanged: () => void;
  currentUserId?: string;
  isAdmin?: boolean;
}

export function AttachmentList({ attachments, uploadPath, onChanged, currentUserId, isAdmin }: AttachmentListProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<FlowAttachment | null>(null);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(uploadPath, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : undefined, body: form });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.message || 'Falha no upload');
      }
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao anexar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await flowFetch(`/api/flow/attachments/${id}`, { method: 'DELETE' });
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir anexo');
    }
  };

  const images = attachments.filter(a => kind(a.mimeType) === 'image');

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Anexos</p>
        <label className={`cursor-pointer rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 ${uploading ? 'opacity-50' : ''}`}>
          {uploading ? 'Enviando...' : '+ Anexar arquivo'}
          <input type="file" className="hidden" disabled={uploading}
            onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </label>
      </div>

      {/* Miniaturas das imagens para visualização rápida */}
      {images.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {images.map(a => (
            <button key={a.id} onClick={() => setPreview(a)} className="group relative">
              <img src={a.url} alt={a.fileName} className="h-16 w-16 rounded-lg border border-slate-200 object-cover transition group-hover:opacity-90" />
            </button>
          ))}
        </div>
      )}

      <ul className="space-y-1">
        {attachments.map(a => (
          <li key={a.id} className="group flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5 text-sm">
            <span>{icon(a.mimeType)}</span>
            {isPreviewable(a.mimeType) ? (
              <button onClick={() => setPreview(a)} className="min-w-0 flex-1 truncate text-left text-cyan-700 hover:underline" title={a.fileName}>{a.fileName}</button>
            ) : (
              <a href={a.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-cyan-700 hover:underline" title={a.fileName}>{a.fileName}</a>
            )}
            {a.size ? <span className="shrink-0 text-[10px] text-slate-400">{fmtSize(a.size)}</span> : null}
            {isPreviewable(a.mimeType) && <span className="shrink-0 text-[10px] font-semibold uppercase text-slate-300">ver</span>}
            {(isAdmin || a.uploadedById === currentUserId) && (
              <button onClick={() => remove(a.id)} className="shrink-0 text-slate-300 opacity-0 transition hover:text-red-500 group-hover:opacity-100" title="Excluir anexo">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
          </li>
        ))}
        {attachments.length === 0 && <li className="text-sm text-slate-400">Nenhum anexo.</li>}
      </ul>

      {preview && <AttachmentPreview attachment={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
